import { Localization, parseLocale } from '@btcdirect/functions';
import { useEnv } from '@btcdirect/shared/src/composables/util/env';
import { computed, reactive } from 'vue';
import { useSystemInfo } from '../api/system/system-info';

interface LocaleInfo extends Localization {
    fallback: string;
}

const DEFAULT_LOCALE = useEnv().getString('VITE_DEFAULT_LOCALE', 'en-GB').toLowerCase();

export const MAPPED_LOCALES: { [key: string]: string } = {
    de: 'de-at',
    en: 'en-gb',
    es: 'es-es',
    fr: 'fr-fr',
    nl: 'nl-nl',
    pt: 'pt-pt'
};

const state = reactive<LocaleInfo>({
    locale: null,
    country: null,
    language: null,
    fallback: DEFAULT_LOCALE
});

export const useLocale = () => {
    const setLocale = (locale: string): void => {
        const { supportedRegions } = useSystemInfo();

        const localization = parseLocale(locale);

        // If the system info call hasn't been done yet, accept the current locale
        if (!supportedRegions.value.length) {
            setState(localization);
            return;
        }

        // If the locale exists in the supported regions, accept the locale
        if (!!localization.locale && supportedRegions.value.includes(localization.locale)) {
            setState(localization);
            return;
        }

        // If the locale has a language we accept, use the existing locale that matches the language
        // If the language cannot be found, use the fallback locale
        const existingLocale: string | undefined = MAPPED_LOCALES[localization.language];

        if (!!existingLocale) {
            setState(parseLocale(existingLocale));
            return;
        }

        // When a not-allowed value is provided, defer to the fallback locale
        setState(parseLocale(state.fallback));
    };

    function setState(localization: Localization): void {
        state.locale = localization.locale;
        state.country = localization.country;
        state.language = localization.language;
    }

    return {
        locale: computed<string>(() => state.locale),
        country: computed<string>(() => state.country),
        language: computed<string>(() => state.language),
        fallback: computed<string>(() => state.fallback),
        setLocale
    };
};
